import { useState } from 'react';
import { Alert, Button, Card } from '../../components/ui.js';
import { useAskGlobalDebug } from '../../api/debug.js';

const fieldClass =
  'rounded-md border border-secondary/20 bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary';

/**
 * Cross-project debug: ask Claude a question that spans every project — no
 * project is selected. The API assembles a summary of all projects (statuses,
 * deploys, repos, story counts, and a tech-stack fingerprint read from each
 * repo) so it can answer things like "which projects use Lambda?".
 */
export function GlobalDebugPage() {
  const [prompt, setPrompt] = useState('');
  const ask = useAskGlobalDebug();
  const canAsk = prompt.trim().length > 0 && !ask.isPending;

  return (
    <Card>
      <h1 className="mb-1 font-title text-2xl tracking-wide">Debug — all projects</h1>
      <p className="mb-4 text-sm opacity-70">
        Ask a question across every project. Claude sees each project’s status, deployment,
        repository, stories, and a fingerprint of its tech stack read from the repo — and it can
        check live AWS (CloudWatch logs, ECS service health, alarms) — so you can ask things like
        “which projects use Lambda?”, “is anything unhealthy right now?”, or “how many are deployed
        but have no tested stories?”. For a single project, use its own Debug tab.
      </p>

      <div className="flex flex-col gap-3">
        <textarea
          className={`min-h-24 ${fieldClass}`}
          placeholder="e.g. Which projects use AWS Lambda?"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <div>
          <Button disabled={!canAsk} onClick={() => ask.mutate(prompt.trim())}>
            {ask.isPending ? 'Asking Claude…' : 'Ask Claude'}
          </Button>
        </div>

        {ask.isError ? (
          <Alert>Could not get an answer. The assistant may be unavailable.</Alert>
        ) : null}

        {ask.data ? (
          <div className="rounded-md border border-secondary/20 bg-neutral/40 p-4">
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{ask.data.answer}</p>
            <p className="mt-3 text-xs opacity-60">
              {ask.data.model} · {ask.data.usage.inputTokens + ask.data.usage.outputTokens} tokens
              {ask.data.toolCalls ? ` · ${ask.data.toolCalls} AWS check(s)` : ''}
            </p>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
